import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { getServicesAPI } from "../../../../../../utils/APIs/servicesApis";
import { AssignPhotographerToBookingAPI } from "../../../../../../utils/APIs/bookingsApis";

/**
 * Top-level modal hook for Editing.
 */
const useEditingFloat = ({ booking, isOpen, onClose, refetchData }) => {
    const [loadingAddBooking, setLoadingAddBooking] = useState(false);
    const [services, setServices]                   = useState([]);

    // ─── Package Selections ──────────────────────────────────────────────────────
    // ─── Package Selections ──────────────────────────────────────────────────────
    const [selectedPackageIndex, setSelectedPackageIndex] = useState(0);
    const [allPackagesSelections, setAllPackagesSelections] = useState({});
    const [refreshVersion, setRefreshVersion] = useState(0);

    // ─── Aggregated Packages ──────────────────────────────────────────────────────
    const rawPackages = booking?.videoPackages || [];
    const packages = [];
    const stds = rawPackages.filter(p => (p.type || "").toLowerCase() === "standard");
    const prems = rawPackages.filter(p => (p.type || "").toLowerCase() === "premium");
    if (stds.length > 0) packages.push({ type: "Standard", originalIndices: stds.map(p => rawPackages.indexOf(p)) });
    if (prems.length > 0) packages.push({ type: "Premium", originalIndices: prems.map(p => rawPackages.indexOf(p)) });

    const handleRoleSelectionChange = (roleKey, ids) => {
        setAllPackagesSelections((prev) => ({
            ...prev,
            [selectedPackageIndex]: {
                ...(prev[selectedPackageIndex] || {}),
                [roleKey]: ids
            }
        }));
    };

    // ─── Services ─────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!isOpen) return;
        (async () => {
            try {
                const res = await getServicesAPI();
                setServices(res?.data?.data || []);
            } catch (err) {
                console.error("Failed to fetch services", err);
            }
        })();
    }, [isOpen]);

    const handleRoleSubmit = async (roleKey, actionType, photographerDetails) => {
        const pkgSelections = allPackagesSelections[selectedPackageIndex] || {};
        const ids = pkgSelections[roleKey] || [];

        if (ids.length === 0) {
            toast.error(`Please select at least one ${roleKey}`);
            return;
        }

        try {
            setLoadingAddBooking(true);

            // Get current aggregated package
            const pkg = packages[selectedPackageIndex];
            const dbPackageIndex =
                pkg?.originalIndices?.[0] ?? selectedPackageIndex;

            const payload = {
                bookingId: booking.bookingId || booking._id,
                packageId: pkg?.packageId || pkg?._id, // Might be null for aggregated
                packageIndex: dbPackageIndex,
                planType: pkg?.type,
                roleSelections: {
                    [roleKey]: ids
                },
                photographerDetails: photographerDetails,
                actionType: "request",
            };

            const response = await AssignPhotographerToBookingAPI(payload);

            if (response?.data?.success) {
                toast.success(response?.data?.message || "Request sent successfully!");
                if (typeof refetchData === 'function') refetchData();
                
                // Clear selection for THIS role and THIS package only
                setAllPackagesSelections((prev) => ({
                    ...prev,
                    [selectedPackageIndex]: {
                        ...(prev[selectedPackageIndex] || {}),
                        [roleKey]: []
                    }
                }));
                setRefreshVersion(v => v + 1);
            } else {
                toast.error(response?.data?.message || "Failed to process action!");
            }
        } catch (err) {
            console.error("request error:", err);
            toast.error("Something went wrong");
        } finally {
            setLoadingAddBooking(false);
        }
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        // Global submit should only send request, never assign directly.
        handleRoleSubmit('editor', 'request');
    };

    const resetAll = () => {
        setAllPackagesSelections({});
        setSelectedPackageIndex(0);
        setRefreshVersion(v => v + 1);
    };

    return {
        services,
        loadingAddBooking,
        selectedPackageIndex,
        setSelectedPackageIndex,
        handleRoleSelectionChange,
        handleSubmit,
        handleRoleSubmit,
        resetAll,
        allPackagesSelections,
        refreshVersion,
    };
};

export default useEditingFloat;
